import React, { ReactElement } from "react";
import { Box, SvgIconProps, Theme } from "@mui/material";
import { Account, AccountTypeEnum } from "@bn/api/apiv1";
//#region Common icons
import AssetsIcon from "@icons/myicons/gold.svg?react";
import DepotIcon from "@icons/myicons/chart-square-1.svg?react";
import CashIcon from "@icons/myicons/cash.svg?react";
import InsuranceIcon from "@icons/myicons/insurance.svg?react";
import RefIcon from "@icons/myicons/arrow-down-right.svg?react";
import HomeIcon from "@icons/myicons/home-house.svg?react";
//#endregion
//#region Cashflow icons
import LifestyleIcon from "@icons/myicons/lifestyle.svg?react";
import ShoppingIcon from "@icons/myicons/shopping.svg?react";
import BankCreditIcon from "@icons/myicons/bank-and-credit.svg?react";
import HealthIcon from "@icons/myicons/health.svg?react";
import CustomIcon from "@icons/myicons/custom.svg?react";
import TravelIcon from "@icons/myicons/travel.svg?react";
import FinanceIcon from "@icons/myicons/contract/finance.svg?react";
import SavingsIcon from "@icons/myicons/contract/savings.svg?react";
import LeisureIcon from "@icons/myicons/contract/leisure.svg?react";
import ChildrenIcon from "@icons/myicons/contract/children.svg?react";
import CarIcon from "@icons/myicons/contract/car.svg?react";
import NotClassifiedThinIcon from "@icons/myicons/contract-thin/not-classified-thin.svg?react";
//#endregion
import { BankAvatar } from "@components";

const COMMMON_ICON_SIZE = { width: "24px", height: "24px" };
const CONTRACTS_ICON_SIZE = { width: "20px", height: "20px" };

export const hasDefaultIcon = (url: string) =>
  url.includes("DEPOT_square") ||
  url.includes("CHECKING_square") ||
  url.includes("INSURANCE_square") ||
  url.includes("MATERIAL_ASSETS_square");

export const accountTypeIconMapper: {
  [key in AccountTypeEnum]: ReactElement<SvgIconProps>;
} = {
  "01_depot": <DepotIcon {...COMMMON_ICON_SIZE} />,
  "02_cash": <CashIcon {...COMMMON_ICON_SIZE} />,
  "04_insurance": <InsuranceIcon {...COMMMON_ICON_SIZE} />,
  "05_material_assets": <AssetsIcon {...COMMMON_ICON_SIZE} />,
};

export const cashflowAnalyzerIconMapper: Record<string, ReactElement> = {
  UNKNOWN: <NotClassifiedThinIcon {...CONTRACTS_ICON_SIZE} />,
  BANK_AND_CREDIT: <BankCreditIcon {...CONTRACTS_ICON_SIZE} />,
  Custom: <CustomIcon {...CONTRACTS_ICON_SIZE} />,
  INCOME: <FinanceIcon {...CONTRACTS_ICON_SIZE} />,
  LEISURE: <LeisureIcon {...CONTRACTS_ICON_SIZE} />,
  HEALTH_AND_WELLNESS: <HealthIcon {...CONTRACTS_ICON_SIZE} />,
  CHILDREN: <ChildrenIcon {...CONTRACTS_ICON_SIZE} />,
  COST_OF_LIVING: <LifestyleIcon {...CONTRACTS_ICON_SIZE} />,
  MOBILITY: <CarIcon {...CONTRACTS_ICON_SIZE} />,
  TRAVELLIING: <TravelIcon {...CONTRACTS_ICON_SIZE} />,
  SHOPPING_AND_ENTERTAINMENT: <ShoppingIcon {...CONTRACTS_ICON_SIZE} />,
  SAVING_AND_INVESTING: <SavingsIcon {...CONTRACTS_ICON_SIZE} />,
  INSURANCE: <InsuranceIcon {...CONTRACTS_ICON_SIZE} />,
  HOME_AND_LIVING: <HomeIcon {...CONTRACTS_ICON_SIZE} />,
};

export const cashflowAnalyzerSlugIconMapper: Record<string, ReactElement> = {
  UNKNOWN: <NotClassifiedThinIcon {...CONTRACTS_ICON_SIZE} />,
  BANK_AND_CREDIT: <BankCreditIcon {...CONTRACTS_ICON_SIZE} />,
  INCOME: <FinanceIcon {...CONTRACTS_ICON_SIZE} />,
  LEISURE: <LeisureIcon {...CONTRACTS_ICON_SIZE} />,
  HEALTH_AND_WELLNESS: <HealthIcon {...CONTRACTS_ICON_SIZE} />,
  CHILDREN: <ChildrenIcon {...CONTRACTS_ICON_SIZE} />,
  COST_OF_LIVING: <LifestyleIcon {...CONTRACTS_ICON_SIZE} />,
  MOBILITY: <CarIcon {...CONTRACTS_ICON_SIZE} />,
  SHOPPING_AND_ENTERTAINMENT: <ShoppingIcon {...CONTRACTS_ICON_SIZE} />,
  SAVING_AND_INVESTING: <SavingsIcon {...CONTRACTS_ICON_SIZE} />,
  INSURANCE: <InsuranceIcon {...CONTRACTS_ICON_SIZE} />,
  HOME_AND_LIVING: <HomeIcon {...CONTRACTS_ICON_SIZE} />,
};

export const ReferenceIcon = () => <RefIcon {...COMMMON_ICON_SIZE} />;

export const getAccountIcon = (
  theme: Theme,
  size: number,
  account: Account,
  alwaysDisplayIcon: boolean = false
) => {
  if (!account) return <></>;
  const iconSizes = { width: `${size}px`, height: `${size}px` };

  if (account.depotIds?.length && !alwaysDisplayIcon) {
    return (
      <Box
        sx={{
          ...iconSizes,
          "& svg": iconSizes,
        }}
        color={theme.palette.tertiary.main}
      >
        <ReferenceIcon />
      </Box>
    );
  }

  if (!hasDefaultIcon(account.iconUrl)) {
    return <BankAvatar imgUrl={account.iconUrl} size={size} />;
  }

  return (
    <Box
      sx={{
        ...iconSizes,
        "& svg": iconSizes,
      }}
      color={theme.palette.tertiary.dark}
    >
      {accountTypeIconMapper[account.type as AccountTypeEnum]}
    </Box>
  );
};
